//	Geometry for cartesian and pie charts. Deterministic and DOM-free: the same estimate-based
//	text widths run on SSR and in the browser, so a hydrated chart matches its SSR twin.
import { linear, band } from "./scale.js";
import { niceTicks, tickCount, thinLabels } from "./ticks.js";
import { stackSeries, stackExtent, stackTotals } from "./stack.js";
import { formatTick, formatValue, formatDateLabel, unitLabel } from "./format.js";
import { linePath, areaPath, roundedBar, arc } from "./path.js";
import { seriesVar, ACCENT_VAR } from "./palette.js";
import { isNum } from "./model.js";

export const FONT = 12;
export const CHAR_W = 6.5;
const BAR_MAX = 24;
const GAP = 2;
const RADIUS = 4;
const DOT = 4;

export function textWidth(text, size = FONT) {
	// A conservative deterministic estimate works on SSR and with native font
	// fallbacks. Wide glyphs must not receive the same budget as narrow ones.
	return [...String(text ?? "")].reduce((sum, c) => sum + (/[MW@%mw]/.test(c) ? 10.8 : /[A-ZĂÂÎȘȚ]/.test(c) ? 8.5 : /[0-9−]/.test(c) ? 7.3 : CHAR_W), 0) * size / FONT;
}

export function truncate(text, maxPx) {
	const s = String(text ?? "");
	if (textWidth(s) <= maxPx) return s;
	if (maxPx < textWidth("…")) return "";
	let result = "";
	for (const char of s) {
		if (textWidth(result + char + "…") > maxPx) break;
		result += char;
	}
	return result + "…";
}

//	the plot box height is a pure function of (data, device): legend toggles and widths never move it
export function heightFor(type, spec, options = {}, compact = false) {
	if (type === "bar") {
		const n = Math.max(1, spec.x.length);
		const k = Math.max(1, spec.series.length);
		const groupH = options.stacked || k === 1 ? BAR_MAX : k * BAR_MAX + GAP * (k - 1);
		const rowH = Math.max(14, Math.min(groupH + 12, 1000 / n));
		return Math.round(Math.min(1040, 16 + n * rowH + 28));
	}
	if (type === "pie" || type === "donut") return compact ? 260 : 320;
	return compact ? 260 : 360;
}

const colorOf = (series, single) => (single ? ACCENT_VAR : seriesVar(series.color));

export function layoutCartesian({ type, spec, options = {}, annotations = [], width, height, hidden = [], compact = false }) {
	const horizontal = type === "bar";
	const isLine = type === "line" || type === "area";
	const n = spec.x.length;
	const single = spec.series.length === 1;
	const visible = spec.series.map((s, idx) => ({ ...s, idx })).filter((s) => !hidden.includes(s.idx));
	const values = visible.map((s) => s.values);
	const stacked = !!options.stacked && visible.length > 1 && type !== "line";
	const stacks = stacked ? stackSeries(values) : null;
	const totals = stacked ? stackTotals(values) : null;

	//	value domain
	let vmin = Infinity;
	let vmax = -Infinity;
	if (stacked) [vmin, vmax] = stackExtent(stacks);
	else for (const s of values) for (const v of s) if (isNum(v)) { vmin = Math.min(vmin, v); vmax = Math.max(vmax, v); }
	if (vmin === Infinity) { vmin = 0; vmax = 1; }
	for (const a of annotations) if (a && a.kind === "line" && isNum(a.value)) { vmin = Math.min(vmin, a.value); vmax = Math.max(vmax, a.value); }
	const includeZero = type !== "line";
	let hardMin = isNum(options.yMin) ? options.yMin : null;
	let hardMax = isNum(options.yMax) ? options.yMax : null;
	if (hardMin !== null && hardMax !== null && hardMin >= hardMax) hardMin = hardMax = null;
	if (includeZero) {
		if (hardMin === null) vmin = Math.min(0, vmin);
		if (hardMax === null) vmax = Math.max(0, vmax);
	}
	if (hardMin !== null) vmin = hardMin;
	if (hardMax !== null) vmax = hardMax;
	// A one-sided bound may exclude all data. Still give the remaining automatic
	// side a nonzero span, preserving the explicitly requested bound.
	if (vmin >= vmax) {
		const pad = Math.max(1, Math.abs(vmin) * 0.05, Math.abs(vmax) * 0.05);
		if (hardMin !== null) vmax = vmin + pad;
		else vmin = vmax - pad;
	}
	const plotExtent = horizontal ? width : height;
	const nice = niceTicks(vmin, vmax, tickCount(Math.max(120, plotExtent - 40)), includeZero && hardMin === null && hardMax === null);
	const dmin = hardMin !== null ? hardMin : nice.min;
	const dmax = hardMax !== null ? hardMax : nice.max;
	const ticks = nice.ticks.filter((t) => t >= dmin - 1e-9 && t <= dmax + 1e-9);
	if (!ticks.length) ticks.push(dmin, dmax);
	const tickLabels = ticks.map((t) => formatTick(t, options));
	//	percent ticks already carry their sign; other units go once into the corner caption
	const unit = options.yFormat === "percent" ? "" : unitLabel(options);
	const decimals = isNum(options.decimals) ? options.decimals : 0;
	//	direct labels drop the unit (it is in the caption); the tooltip and table keep it
	const fmt = (v) => formatValue(v, { ...options, decimals, unit: undefined, currency: undefined, yFormat: options.yFormat === "percent" ? "percent" : "number" });

	//	margins
	const xLabelsText = spec.x.map((l) => (options.xKind === "date" ? formatDateLabel(l, compact) : l));
	const maxTickW = Math.max(0, ...tickLabels.map((t) => textWidth(t)));
	const maxCatW = Math.max(0, ...xLabelsText.map((t) => textWidth(t)));
	const maxValW = Math.max(0, ...values.flat().filter(isNum).map((v) => textWidth(fmt(v))));
	let left, right, top, bottom;
	if (!horizontal) {
		left = Math.ceil(maxTickW) + 10;
		right = 12 + (isLine && options.showValues ? Math.ceil(Math.max(...visible.map((s) => textWidth(fmt(lastNum(s.values)))), 0)) + 6 : 0);
		top = 10 + (unit ? 18 : 0) + (!isLine && options.showValues ? 14 : 0);
		bottom = 24;
	} else {
		left = Math.ceil(Math.min(0.4 * width, maxCatW + 10));
		right = 12 + (options.showValues ? Math.ceil(maxValW) + 6 : 0);
		top = 8 + (unit ? 18 : 0);
		bottom = 24;
	}
	const plot = { x: left, y: top, w: Math.max(10, width - left - right), h: Math.max(10, height - top - bottom) };

	//	scales
	const vScale = horizontal ? linear([dmin, dmax], [plot.x, plot.x + plot.w]) : linear([dmin, dmax], [plot.y + plot.h, plot.y]);
	const catRange = horizontal ? [plot.y, plot.y + plot.h] : [plot.x, plot.x + plot.w];
	let xAt, slot, bandObj = null;
	if (isLine) {
		slot = n > 1 ? (catRange[1] - catRange[0]) / (n - 1) : catRange[1] - catRange[0];
		xAt = (i) => (n > 1 ? catRange[0] + i * slot : (catRange[0] + catRange[1]) / 2);
	} else {
		bandObj = band(n, catRange, 0.3, 0.15);
		slot = bandObj.step;
		xAt = (i) => bandObj.center(i);
	}
	const zeroIn = dmin <= 0 && dmax >= 0;
	const basePos = vScale(zeroIn ? 0 : dmin < 0 ? dmax : dmin);

	const grid = ticks.map((t, i) => ({ value: t, label: tickLabels[i], pos: vScale(t), zero: t === 0 }));

	//	marks
	const lines = [];
	const areas = [];
	const bars = [];
	const dots = [];
	const valueLabels = [];
	if (isLine) {
		visible.forEach((s, vi) => {
			const color = colorOf(s, single);
			let upper, lower;
			if (stacked) {
				upper = s.values.map((v, i) => (stacks[vi][i] ? { x: xAt(i), y: vScale(stacks[vi][i].y1), v } : null));
				lower = s.values.map((v, i) => (stacks[vi][i] ? { x: xAt(i), y: vScale(stacks[vi][i].y0) } : null));
			} else {
				upper = s.values.map((v, i) => (isNum(v) ? { x: xAt(i), y: vScale(v), v } : null));
			}
			const end = lastPoint(upper);
			lines.push({ idx: s.idx, name: s.name, color, d: linePath(upper), points: upper, end });
			if (type === "area") areas.push({ idx: s.idx, color, d: stacked ? bandPath(upper, lower) : areaPath(upper, basePos) });
			//	isolated points (gaps on both sides) would be invisible as a line
			upper.forEach((p, i) => { if (p && !upper[i - 1] && !upper[i + 1]) dots.push({ idx: s.idx, color, x: p.x, y: p.y }); });
			if (options.showValues && end && end.y >= plot.y && end.y <= plot.y + plot.h && visible.length <= 5) valueLabels.push({ x: end.x + 6, y: end.y, text: fmt(end.v), anchor: "start", baseline: "middle", idx: s.idx });
		});
		//	converging end labels: keep the first, drop the ones closer than a line of text
		valueLabels.sort((a, b) => a.y - b.y);
		for (let i = valueLabels.length - 1; i > 0; i--) if (valueLabels[i].y - valueLabels[i - 1].y < 13) valueLabels.splice(i, 1);
	} else {
		const k = visible.length;
		const bandwidth = bandObj.bandwidth;
		const thick = stacked || k === 1 ? Math.min(BAR_MAX, bandwidth) : Math.min(BAR_MAX, Math.max(2, (bandwidth - GAP * (k - 1)) / k));
		const groupW = stacked || k === 1 ? thick : k * thick + GAP * (k - 1);
		for (let i = 0; i < n; i++) {
			const center = xAt(i);
			//	last segment of each stack direction gets the rounded data end
			let lastPos = -1, lastNeg = -1;
			if (stacked) visible.forEach((s, vi) => { const seg = stacks[vi][i]; if (!seg) return; if (seg.y1 >= seg.y0) lastPos = vi; else lastNeg = vi; });
			visible.forEach((s, vi) => {
				let v0, v1;
				if (stacked) { const seg = stacks[vi][i]; if (!seg) return; v0 = seg.y0; v1 = seg.y1; }
				else { const v = s.values[i]; if (!isNum(v)) return; v0 = zeroIn ? 0 : dmin < 0 ? dmax : dmin; v1 = v; }
				const positive = v1 >= v0;
				const outer = !stacked || (positive ? vi === lastPos : vi === lastNeg);
				const inner = stacked && v0 !== 0;
				let a = vScale(v0), b = vScale(v1);
				//	clamp into the plot when hard bounds cut a bar
				const lo = horizontal ? plot.x : plot.y, hi = horizontal ? plot.x + plot.w : plot.y + plot.h;
				a = Math.max(lo, Math.min(hi, a)); b = Math.max(lo, Math.min(hi, b));
				const offset = stacked || k === 1 ? center - thick / 2 : center - groupW / 2 + vi * (thick + GAP);
				let rect, side;
				if (!horizontal) {
					let yTop = Math.min(a, b), yBot = Math.max(a, b);
					if (inner) { if (positive) yBot -= GAP; else yTop += GAP; }
					side = positive ? "top" : "bottom";
					rect = { x: offset, y: yTop, w: thick, h: Math.max(0, yBot - yTop) };
				} else {
					let xL = Math.min(a, b), xR = Math.max(a, b);
					if (inner) { if (positive) xL += GAP; else xR -= GAP; }
					side = positive ? "right" : "left";
					rect = { x: xL, y: offset, w: Math.max(0, xR - xL), h: thick };
				}
				const d = roundedBar(rect.x, rect.y, rect.w, rect.h, outer ? RADIUS : 0, side);
				bars.push({ idx: s.idx, cat: i, color: colorOf(s, single), d, rect, v: s.values[i] });
				if (options.showValues && outer && !stacked && v1 >= dmin && v1 <= dmax) {
					const text = fmt(v1);
					if (!horizontal) valueLabels.push({ x: offset + thick / 2, y: positive ? rect.y - 4 : rect.y + rect.h + 12, text, anchor: "middle", baseline: positive ? "auto" : "hanging", idx: s.idx });
					else valueLabels.push({ x: positive ? rect.x + rect.w + 4 : rect.x - 4, y: offset + thick / 2, text, anchor: positive ? "start" : "end", baseline: "middle", idx: s.idx });
				}
			});
			if (options.showValues && stacked && totals && isNum(totals[i]) && totals[i] >= dmin && totals[i] <= dmax) {
				const t = totals[i];
				const pos = vScale(t);
				if (!horizontal) valueLabels.push({ x: center, y: pos - 4, text: fmt(t), anchor: "middle", baseline: "auto" });
				else valueLabels.push({ x: pos + 4, y: center, text: fmt(t), anchor: "start", baseline: "middle" });
			}
		}
	}

	const occupiedValues = [];
	for (let i = 0; i < valueLabels.length;) {
		const v = valueLabels[i], w = textWidth(v.text);
		const x = v.anchor === "end" ? v.x - w : v.anchor === "middle" ? v.x - w / 2 : v.x;
		const y = v.baseline === "hanging" ? v.y : v.baseline === "middle" ? v.y - 6 : v.y - 12;
		const box = { x, y, w, h: 14 };
		if (x < plot.x || x + w > width - 2 || y < 0 || y + 14 > (isLine ? height - 2 : plot.y + plot.h) || occupiedValues.some((b) => x < b.x + b.w + 4 && x + w + 4 > b.x && y < b.y + b.h && y + 14 > b.y)) valueLabels.splice(i, 1);
		else { occupiedValues.push(box); i++; }
	}

	//	category labels
	let labels;
	if (!horizontal) {
		const shown = thinLabels(n, plot.w, Math.min(maxCatW, Math.max(24, plot.w / 3 - 8)));
		labels = shown.map((i, k) => {
			const center = xAt(i);
			const left = k ? (xAt(shown[k - 1]) + center) / 2 + 4 : 2;
			const right = k < shown.length - 1 ? (center + xAt(shown[k + 1])) / 2 - 4 : width - 2;
			const text = truncate(xLabelsText[i], Math.max(6, right - left));
			const half = textWidth(text) / 2;
			return { i, pos: Math.max(left + half, Math.min(right - half, center)), anchor: "middle", text, full: xLabelsText[i] };
		});
	} else {
		labels = xLabelsText.map((t, i) => ({ i, pos: xAt(i), text: truncate(t, left - 10), full: t }));
	}

	//	annotations
	const refLines = [];
	const notes = [];
	for (const a of annotations || []) {
		if (!a) continue;
		//	label sits left when end-value labels occupy the right edge
		if (a.kind === "line" && isNum(a.value) && a.value >= dmin && a.value <= dmax) refLines.push({ pos: vScale(a.value), label: truncate(a.label || fmt(a.value), horizontal ? width - vScale(a.value) - 8 : plot.w - 8), full: a.label || fmt(a.value), value: a.value, left: isLine && !!options.showValues });
		if (a.kind === "point") {
			const s = visible.find((v) => v.col === a.column);
			const v = s?.values?.[a.row];
			if (!s || !isNum(v) || a.row < 0 || a.row >= n) continue;
			const val = stacked ? stacks[visible.indexOf(s)][a.row]?.y1 : v;
			if (!isNum(val) || val < dmin || val > dmax) continue;
			const px = xAt(a.row), py = vScale(val);
			const [x, y] = horizontal ? [py, px] : [px, py];
			const flip = x > plot.x + plot.w * 0.6;
			notes.push({ x, y, label: truncate(a.label || "", flip ? x - plot.x - 8 : plot.x + plot.w - x - 8), full: a.label || "", flip, color: colorOf(s, single) });
		}
	}

	const hitIndex = (px, py) => {
		if (!n) return -1;
		const p = horizontal ? py : px;
		if (isLine) return Math.max(0, Math.min(n - 1, Math.round((p - catRange[0]) / (slot || 1))));
		return bandObj.index(p);
	};

	return { plot, horizontal, isLine, stacked, single, visible, dmin, dmax, grid, basePos, zeroIn, slot, xAt, labels, lines, areas, bars, dots, valueLabels, refLines, notes, unit, totals, hitIndex, dotRadius: DOT, catLabels: xLabelsText };
}

function lastNum(values) { for (let i = values.length - 1; i >= 0; i--) if (isNum(values[i])) return values[i]; return null; }
function lastPoint(points) { for (let i = points.length - 1; i >= 0; i--) if (points[i]) return points[i]; return null; }

//	stacked area: polygon between the upper and lower lines per contiguous run
function bandPath(upper, lower) {
	let d = "";
	let run = [];
	const flush = () => {
		if (!run.length) return;
		d += "M" + run.map((i) => `${h(upper[i].x)} ${h(upper[i].y)}`).join("L");
		for (let j = run.length - 1; j >= 0; j--) d += "L" + h(lower[run[j]].x) + " " + h(lower[run[j]].y);
		d += "Z";
		run = [];
	};
	for (let i = 0; i < upper.length; i++) { if (upper[i] && lower[i]) run.push(i); else flush(); }
	flush();
	return d;
}
const h = (v) => Math.round(v * 2) / 2;

export const OTHER_LABEL = "Altele";

export function layoutPie({ type, spec, options = {}, width, height, hidden = [] }) {
	const s = spec.series[0];
	const values = s ? s.values : [];
	let items = spec.x.map((label, i) => ({ i, label, value: values[i] })).filter((it) => isNum(it.value) && it.value > 0);
	let folded = null;
	if (items.length > 8) {
		const sorted = items.slice().sort((a, b) => b.value - a.value);
		const keep = new Set(sorted.slice(0, 7).map((it) => it.i));
		const rest = items.filter((it) => !keep.has(it.i));
		folded = { i: -1, label: OTHER_LABEL, value: rest.reduce((t, it) => t + it.value, 0), members: rest.map((it) => it.i) };
		items = items.filter((it) => keep.has(it.i));
	}
	//	legend/slot order is the editorial order; slots follow the kept index so a hidden slice never repaints its neighbours
	const all = folded ? [...items, folded] : items;
	all.forEach((it, k) => { it.slot = k + 1; it.color = it.i === -1 ? "var(--_other)" : seriesVar(k + 1); });
	const shown = all.filter((it) => !hidden.includes(it.i));
	const total = shown.reduce((t, it) => t + it.value, 0);
	const cx = width / 2, cy = height / 2;
	const r1 = Math.max(10, Math.min(width, height) / 2 - 8);
	const r0 = type === "donut" ? Math.round(r1 * 0.62) : 0;
	const decimals = isNum(options.decimals) ? options.decimals : 0;
	let a = 0;
	const slices = shown.map((it) => {
		const share = total ? it.value / total : 0;
		const a0 = a, a1 = a + share * Math.PI * 2;
		a = a1;
		const mid = (a0 + a1) / 2;
		const lr = r0 ? (r0 + r1) / 2 : r1 * 0.66;
		const pct = `${Math.round(share * 100)}%`;
		const showLabel = share >= 0.08 && (a1 - a0) * lr > textWidth(pct) + 6;
		return { ...it, share, a0, a1, mid, d: arc(cx, cy, r0, r1, a0, a1), lx: cx + lr * Math.sin(mid), ly: cy - lr * Math.cos(mid), pct, showLabel, text: formatValue(it.value, { ...options, decimals }), dx: Math.sin(mid), dy: -Math.cos(mid) };
	});
	const fullTotal = formatValue(total, { ...options, decimals });
	const totalFontSize = Math.max(10, Math.min(22, 22 * r0 * 1.7 / Math.max(1, textWidth(fullTotal, 22))));
	const totalText = truncate(fullTotal, r0 * 1.7 * FONT / totalFontSize);
	return { cx, cy, r0, r1, total, totalText, fullTotal, totalFontSize, slices, legend: all, folded };
}
